import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import UserChatbox from "../pages/ChatBox/UserChatbox";
import ImpersonationBanner from "./ImpersonationBanner";

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [chatboxOpen, setChatboxOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Check if impersonating
  useEffect(() => {
    const checkImpersonation = () => {
      const impersonating = localStorage.getItem("isImpersonating");
      setIsImpersonating(impersonating === "true");
    };

    checkImpersonation();
    
    // Listen for storage changes
    window.addEventListener('storage', checkImpersonation);
    return () => window.removeEventListener('storage', checkImpersonation);
  }, []);

  // Fetch user data for chatbox
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.user) {
            setUserData(data.user);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 transition-colors duration-300">
      {/* Impersonation Banner */}
      <ImpersonationBanner sidebarCollapsed={sidebarCollapsed} />
      
      {/* Sidebar (Toggles on Mobile) */}
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />
      
      {/* Main Content Area */}
      <div className={`min-h-screen flex flex-col transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'
      } ${isImpersonating ? 'pt-16' : ''}`}>
        {/* Header (Has Sidebar Toggle Button on Mobile) */}
        <Header 
          setSidebarOpen={setSidebarOpen} 
          chatboxOpen={chatboxOpen}
          setChatboxOpen={setChatboxOpen}
        />
        
        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-x-hidden">
          <div className="max-w-7xl mx-auto animate-fade-in">
            <Outlet context={{ setChatboxOpen }} />
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Chatbox - Rendered at root level for proper z-index stacking */}
      {chatboxOpen && userData && (
        <UserChatbox 
          user={userData} 
          onClose={() => setChatboxOpen(false)} 
        />
      )}
    </div>
  );
};

export default Layout;
